import type { SocialProvider } from '@words-happen/ui-components';
import { loadEmailAuth } from './load-email-auth';
import type { VerificationOptions } from './sign-in-with-email';

/** sessionStorage key set while a social sign-in redirect started on this tab may be waiting to be completed. */
export const SOCIAL_REDIRECT_KEY = 'wh:social-redirect';

/**
 * Remembers, for this tab, that a social sign-in redirect started here, so the page it returns to knows to complete
 * it without loading Firebase on every visit. Never throws: storage that is blocked counts as nothing pending.
 */
export interface SocialRedirectStore {
  pending: () => boolean;
  mark: () => void;
  clear: () => void;
}

type RedirectStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** A store over `storage()`, which is called on every access and may throw (window.sessionStorage can). */
export const createSocialRedirectStore = (storage: () => RedirectStorage | undefined): SocialRedirectStore => ({
  pending: () => {
    try {
      const value = storage()?.getItem(SOCIAL_REDIRECT_KEY);
      return value !== null && value !== undefined;
    } catch {
      return false;
    }
  },
  mark: () => {
    try {
      storage()?.setItem(SOCIAL_REDIRECT_KEY, '1');
    } catch {
      // Firebase needs the same storage for the redirect, so signInWithRedirect fails and says why.
    }
  },
  clear: () => {
    try {
      storage()?.removeItem(SOCIAL_REDIRECT_KEY);
    } catch {
      // Nothing could be stored either.
    }
  },
});

export const browserSocialRedirectStore = createSocialRedirectStore(() => window.sessionStorage);

/**
 * A player signed in with a social provider. An unverified one has just been sent the verification email, and
 * `verificationSent` is false when that failed, so /verify-email offers to resend it.
 */
export type SocialSignInResult = { verified: true } | { verified: false; verificationSent: boolean };

/** Identity Platform's redirect sign-in with a social provider; each call rejects with the Firebase error. */
export interface SocialRedirect {
  /** Leaves for the provider's sign-in page (signInWithRedirect), returning to this page's URL. */
  redirect: (provider: SocialProvider) => Promise<void>;
  /**
   * The redirect's outcome once back (getRedirectResult): the signed-in player, or null when there is none. An
   * unverified player is sent the verification email, continuing to `options.continueUrl`.
   */
  redirectResult: (options: VerificationOptions) => Promise<SocialSignInResult | null>;
}

/** Social sign-in for a page: starting a redirect, and completing one on the page it returns to. */
export interface SocialSignIn {
  /** Whether a redirect started on this tab may be waiting to be completed. Loads nothing. */
  redirectPending: () => boolean;
  /** Leaves for `provider`'s sign-in page. Rejects with the Firebase error when it can't start. */
  start: (provider: SocialProvider) => Promise<void>;
  /**
   * Completes a pending redirect, once: the signed-in player, or null when there is none (the player came back
   * without signing in). An unverified player is sent the verification email, continuing to `options.continueUrl`.
   * Rejects with the Firebase error, e.g. the sign-up allowlist's refusal.
   */
  finish: (options: VerificationOptions) => Promise<SocialSignInResult | null>;
}

/** Social sign-in through `redirect`, remembering a pending redirect in `store`. */
export const createSocialSignIn = (
  store: SocialRedirectStore,
  redirect: () => Promise<SocialRedirect>,
): SocialSignIn => ({
  redirectPending: store.pending,

  start: async (provider) => {
    store.mark();
    try {
      await (await redirect()).redirect(provider);
    } catch (error) {
      store.clear();
      throw error;
    }
  },

  finish: async (options) => {
    // Cleared first, so a result that fails or never comes isn't completed again on the next visit.
    store.clear();
    return (await redirect()).redirectResult(options);
  },
});

/** Identity Platform social sign-in (email-auth.ts), loading the Firebase SDK on first use. */
export const identityPlatformSocialSignIn: SocialSignIn = createSocialSignIn(
  browserSocialRedirectStore,
  async () => (await loadEmailAuth()).socialRedirect,
);
